import { useAuth0 } from '@auth0/auth0-vue'
import * as Sentry from '@sentry/vue'
import { useFetch, UseFetchOptions, UseFetchReturn } from '@vueuse/core'
import { computed, ComputedRef, isRef, unref } from 'vue'

import { appConfig } from '@/config'
import { PER_PAGE } from '@/constants'
import type { PaginationType, MetaType, ApiResponseType } from '@/types'
import { useToast } from '@/ui'

import { formatedField } from './data'
import { normalizeSentryUrl } from './sentry-url'

export function checkIsFullUrl(url: string) {
  return /^https?:\/\//i.test(url)
}

function computeUrl(value: string, withAuth: boolean) {
  return checkIsFullUrl(value) ? value : appConfig.api[withAuth ? 'url' : 'publicUrl'] + value
}

export function ensureHttps(url?: string) {
  if (!url) return url

  const trimmedUrl = url.trim()

  return checkIsFullUrl(trimmedUrl) ? trimmedUrl : `https://${trimmedUrl}`
}

export function getApiPaginationData(meta: MetaType): PaginationType {
  const pageIndex = meta.current_page - 1 || 0
  const pageSize = meta.per_page || PER_PAGE
  const totalItems = meta.total || 0
  const pageCount = meta.last_page || 1

  return { pageIndex, pageSize, totalItems, pageCount }
}

export const validationErrorHandler = (errors: Record<string, string>) => {
  const { toast } = useToast()

  const entries = Object.entries(errors)

  const message = entries.map(([field, msg]) => `${formatedField(field)}: ${msg}`).join(', ')

  const firstErrorFieldName = entries[0]?.[0]

  toast({
    title: 'Validation Error',
    description: message,
    variant: 'warning',
  })

  if (firstErrorFieldName) {
    const element = document.querySelector(`[name="${firstErrorFieldName}"]`)
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    if (element instanceof HTMLElement) {
      element.focus({ preventScroll: true })
    }
  }
}

type UseApiPropsType = {
  url: string | ComputedRef<string>
  method: string
  parseType?: string
  fetchOptions?: RequestInit
  useFetchOptions?: UseFetchOptions
}

export function useApi(withAuth: boolean = true) {
  const { getAccessTokenSilently } = withAuth ? useAuth0() : {}

  function apiFetch<T>({
    url,
    method,
    parseType,
    fetchOptions = {},
    useFetchOptions = {},
  }: UseApiPropsType): ApiResponseType<T> {
    const urlStr = isRef(url) ? computed(() => computeUrl(unref(url), withAuth)) : computeUrl(url, withAuth)
    let parsedAs = parseType || 'json'

    if (method === 'DELETE') {
      parsedAs = 'text'
    }

    const { data, isFetching, isFinished, execute, error } = useFetch<UseFetchReturn<T>>(
      urlStr,
      {
        method,
        ...fetchOptions,
      },
      {
        async beforeFetch({ options }) {
          // Set Accept header based on expected response type
          const acceptHeaders: Record<string, string> = {
            json: 'application/json',
            text: 'text/plain',
            blob: '*/*',
          }

          options.headers = {
            ...options.headers,
            Accept: acceptHeaders[parsedAs] || 'application/json',
          }

          if (typeof getAccessTokenSilently === 'function') {
            const { access_token, id_token } = await getAccessTokenSilently({ detailedResponse: true })
            options.headers = {
              ...options.headers,
              'X-ID-Token': id_token,
              Authorization: `Bearer ${access_token}`,
            }
          }
          return { options }
        },
        afterFetch(ctx) {
          if (ctx.response.ok && method === 'GET' && ctx.data?.meta) {
            ctx.data.pagination = getApiPaginationData(ctx.data.meta)
          }

          return ctx
        },
        updateDataOnError: true,
        onFetchError(ctx) {
          const { error, data, response } = ctx
          let message = 'Unknown error'

          if (response?.status === 422) {
            message = 'Validation error: ' + data?.message
          } else if (data?.message) {
            message = data?.message
          } else if (data?.error) {
            message = data?.error
          } else if (error?.message) {
            message = error?.message
          }
          if (['Fetch is aborted', 'signal is aborted without reason'].includes(message) || response?.status === 200) {
            return ctx
          }

          if (response?.status !== 422) {
            const parsedUrl = typeof url === 'string' ? url : url.value
            const normalizedUrl = normalizeSentryUrl(parsedUrl)

            Sentry.captureException(error, {
              contexts: {
                response: {
                  status: response?.status,
                  data: data,
                },
                request: {
                  url: normalizedUrl, // Use normalized URL to prevent leaking sensitive tokens
                  method: method,
                },
              },
              tags: {
                http_status: response?.status?.toString() || 'unknown',
                endpoint: normalizedUrl, // Use normalized URL to avoid high cardinality
              },
              fingerprint: [method, normalizedUrl, response?.status?.toString() || 'unknown'], // Use normalized URL
              level: 'error',
            })
          }

          const { toast } = useToast()

          toast({
            title: `Error. ${response?.status ? 'Status code: ' + response?.status : ''}`,
            description: message,
            variant: 'destructive',
          })

          return ctx
        },
        ...useFetchOptions,
      }
    )[parsedAs]()

    return {
      execute,
      error: computed(() => error.value),
      isFetching: computed(() => isFetching.value),
      isFinished: computed(() => isFinished.value),
      responseData: computed(() => {
        if (parsedAs === 'text') {
          return {}
        }
        // TODO: remove !data.value?.children
        if (parsedAs !== 'blob' && data.value?.data !== undefined && !data.value?.children) {
          return data.value.data
        }

        return data.value
      }),
      pagination: computed(() => (parsedAs === 'json' ? data.value?.pagination : {})),
    }
  }

  return { apiFetch }
}
